import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../contexts/ClientContext';

interface ModeRouteProps {
  children: React.ReactNode;
  mode: 'internal' | 'client' | 'leadgen';
}

/**
 * ModeRoute - Protects routes based on Internal/Client/LeadGen mode
 * Redirects to home when mode changes and current page is not allowed
 */
export const ModeRoute: React.FC<ModeRouteProps> = ({ children, mode: requiredMode }) => {
  const { mode } = useClient();
  const navigate = useNavigate();

  const isCorrectMode = mode === requiredMode;

  // Redirect when mode changes and we're on wrong page
  useEffect(() => {
    if (!isCorrectMode) {
      navigate('/', { replace: true });
    }
  }, [mode, isCorrectMode, navigate]);

  // Don't render children if wrong mode
  if (!isCorrectMode) {
    return null;
  }

  return <>{children}</>;
};

/**
 * InternalRoute - Shorthand for internal-only routes
 */
export const InternalRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ModeRoute mode="internal">{children}</ModeRoute>
);

/**
 * ClientRoute - Shorthand for client-only routes
 */
export const ClientRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ModeRoute mode="client">{children}</ModeRoute>
);

/**
 * LeadGenRoute - Shorthand for leadgen-only routes
 */
export const LeadGenRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ModeRoute mode="leadgen">{children}</ModeRoute>
);

export default ModeRoute;
